import { useMemo } from 'react';

import {
    BarElement, CategoryScale, Chart as ChartJS, Legend, LineElement,
    LinearScale, PointElement, Tooltip
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

import {
    Alert, Box, Card, CardContent, FormControl, Grid, InputLabel,
    LinearProgress, MenuItem, Select, Stack, Typography
} from '@mui/material';

ChartJS.register(
    BarElement,
    CategoryScale,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip
);

const MetricCard = ({ label, value, helper }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Typography color="text.secondary" variant="body2">
                {label}
            </Typography>
            <Typography sx={{ fontWeight: 700, my: 1 }} variant="h4">
                {value}
            </Typography>
            {helper &&
                <Typography color="text.secondary" variant="caption">
                    {helper}
                </Typography>
            }
        </CardContent>
    </Card>
);

const RankingCard = ({ label, product, emptyText }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Typography color="text.secondary" variant="overline">
                {label}
            </Typography>
            {product ?
                <>
                    <Typography sx={{ fontWeight: 600, mt: 1 }} variant="h6">
                        {product.title}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        {product.average} de 5 · {product.count} reseñas
                    </Typography>
                </>
            :
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                    {emptyText}
                </Typography>
            }
        </CardContent>
    </Card>
);

const ChartCard = ({ title, children }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Typography sx={{ fontWeight: 600, mb: 2 }} variant="subtitle1">
                {title}
            </Typography>
            <Box sx={{ height: 280 }}>{children}</Box>
        </CardContent>
    </Card>
);

const ReviewDashboard = ({ data, error, loading, onRangeChange, range }) => {
    const distributionData = useMemo(() => ({
        labels: ['1 estrella', '2 estrellas', '3 estrellas', '4 estrellas', '5 estrellas'],
        datasets: [{
            label: 'Reseñas',
            data: [1, 2, 3, 4, 5].map(rating =>
                data?.distribution?.[rating] || 0
            ),
            backgroundColor: '#2699FB'
        }]
    }), [data]);
    const trendData = useMemo(() => ({
        labels: (data?.trend || []).map(item => item.period),
        datasets: [
            {
                label: 'Reseñas',
                data: (data?.trend || []).map(item => item.count),
                borderColor: '#2699FB',
                backgroundColor: '#2699FB',
                yAxisID: 'reviews'
            },
            {
                label: 'Promedio',
                data: (data?.trend || []).map(item => item.average),
                borderColor: '#ef6c00',
                backgroundColor: '#ef6c00',
                yAxisID: 'average'
            }
        ]
    }), [data]);
    const reasonsData = useMemo(() => ({
        labels: (data?.hidden_reasons || []).map(item => item.reason),
        datasets: [{
            label: 'Ocultamientos',
            data: (data?.hidden_reasons || []).map(item => item.count),
            backgroundColor: '#d32f2f'
        }]
    }), [data]);
    const chartOptions = {
        maintainAspectRatio: false,
        responsive: true
    };
    const trendOptions = {
        ...chartOptions,
        scales: {
            reviews: {
                beginAtZero: true,
                position: 'left',
                ticks: { precision: 0 }
            },
            average: {
                beginAtZero: true,
                max: 5,
                position: 'right',
                grid: { drawOnChartArea: false }
            }
        }
    };
    const indicators = data?.indicators || {};
    const rankings = data?.rankings || {};

    return (
        <Box>
            <Stack
                alignItems={{ xs: 'stretch', sm: 'center' }}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5">Desempeño de las reseñas</Typography>
                    <Typography color="text.secondary" variant="body2">
                        Sólo las reseñas publicadas participan en los indicadores.
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Periodo</InputLabel>
                    <Select
                        label="Periodo"
                        onChange={({ target }) => onRangeChange(target.value)}
                        value={range}
                    >
                        <MenuItem value="30d">Últimos 30 días</MenuItem>
                        <MenuItem value="90d">Últimos 90 días</MenuItem>
                        <MenuItem value="365d">Últimos 365 días</MenuItem>
                        <MenuItem value="all">Todo el histórico</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error &&
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            }

            {data &&
                <>
                    <Grid container spacing={2}>
                        <Grid item lg={3} sm={6} xs={12}>
                            <MetricCard
                                label="Reseñas publicadas"
                                value={indicators.published || 0}
                            />
                        </Grid>
                        <Grid item lg={3} sm={6} xs={12}>
                            <MetricCard
                                label="Calificación promedio"
                                value={`${indicators.average || 0} / 5`}
                            />
                        </Grid>
                        <Grid item lg={3} sm={6} xs={12}>
                            <MetricCard
                                label="Compras verificadas"
                                value={`${indicators.verified_percentage || 0}%`}
                            />
                        </Grid>
                        <Grid item lg={3} sm={6} xs={12}>
                            <MetricCard
                                helper="Total vigente, independiente del periodo"
                                label="Reseñas ocultas"
                                value={indicators.hidden_current || 0}
                            />
                        </Grid>

                        <Grid item md={4} xs={12}>
                            <RankingCard
                                emptyText="No hay un producto con al menos 3 reseñas."
                                label="Mejor reseñado"
                                product={rankings.best}
                            />
                        </Grid>
                        <Grid item md={4} xs={12}>
                            <RankingCard
                                emptyText="No hay reseñas en el periodo."
                                label="Con más reseñas"
                                product={rankings.most_reviewed}
                            />
                        </Grid>
                        <Grid item md={4} xs={12}>
                            <RankingCard
                                emptyText="No hay un producto con al menos 3 reseñas."
                                label="Producto a vigilar"
                                product={rankings.lowest}
                            />
                        </Grid>

                        <Grid item lg={6} xs={12}>
                            <ChartCard title="Evolución">
                                {(data.trend || []).length > 0 ?
                                    <Line data={trendData} options={trendOptions} />
                                :
                                    <Alert severity="info">Sin datos para graficar.</Alert>
                                }
                            </ChartCard>
                        </Grid>
                        <Grid item lg={6} xs={12}>
                            <ChartCard title="Distribución de calificaciones">
                                <Bar data={distributionData} options={chartOptions} />
                            </ChartCard>
                        </Grid>
                        <Grid item xs={12}>
                            <ChartCard title="Motivos de ocultamiento">
                                {(data.hidden_reasons || []).length > 0 ?
                                    <Bar data={reasonsData} options={chartOptions} />
                                :
                                    <Alert severity="info">
                                        No se ocultaron reseñas en este periodo.
                                    </Alert>
                                }
                            </ChartCard>
                        </Grid>
                    </Grid>
                </>
            }
        </Box>
    );
};

export default ReviewDashboard;
